import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  BudgetItemTypeEnum,
  ContractorSelectOptionsDocument,
  type ContractorSelectOptionsQuery,
  type ContractorSelectOptionsQueryVariables,
  ExpenseInvoiceStatusEnum,
  type ExpenseItemsTimeBillingOrderByColumn,
  ExpenseItemsTimeBillingWhereColumn,
  ExpenseStatusEnum,
  ExpenseTypeEnum,
  type OiBudgetItemsTimeBillingOrderByColumn,
  OiBudgetItemsTimeBillingWhereColumn,
  OutgoingInvoiceTypeEnum,
  type SortOrder,
  SqlOperator,
  TimeBillingExpenseItemsForModalDocument,
  type TimeBillingExpenseItemsForModalQuery,
  type TimeBillingExpenseItemsForModalQueryVariables,
  TimeBillingOiBudgetItemsForModalDocument,
  type TimeBillingOiBudgetItemsForModalQuery,
  type TimeBillingOiBudgetItemsForModalQueryVariables,
  TimeBillingSourcesEditModalFilterOptionsDocument,
  type TimeBillingSourcesEditModalFilterOptionsQuery,
  type TimeBillingSourcesEditModalFilterOptionsQueryVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { appStore } from "@src/stores/AppStore";
import { getPaymentStatusFilterOptions } from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { denominate } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { lowerCase, map, upperFirst } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { isUnsavedId } from "../../store";
import { encodeOiBudgetItemKey } from "./oi-budget-item-key";

type OiBudgetItem = NonNullable<
  TimeBillingOiBudgetItemsForModalQuery["oiBudgetItemsForTimeBilling"]
>["data"][number];

type ExpenseItemForModal = NonNullable<
  TimeBillingExpenseItemsForModalQuery["expenseItemsForTimeBilling"]
>["data"][number];

type DisclosureStateData = {
  initialSelectedTotalAmount: number;
  sourceKeys: string[];
  modalType: BudgetItemTypeEnum;
  onConfirm: (sourceKeys: string[], modalType: BudgetItemTypeEnum) => void;
  timeBillingPlanId: string;
};

export class SourcesEditModalStore {
  @observable.ref
  selectedOiBudgetItemKeys: string[] = [];

  @observable.ref
  selectedExpenseItemIds: string[] = [];

  @observable
  selectedTotalAmount = 0;

  @observable.ref
  timeBillingPlanId?: string;

  @observable.ref
  oiBudgetItemSearchTerm = "";

  oiBudgetItemOrderBy = new OrderBy<OiBudgetItemsTimeBillingOrderByColumn>();

  @observable.ref
  expenseSearchTerm = "";

  expenseOrderBy = new OrderBy<ExpenseItemsTimeBillingOrderByColumn>();

  @observable.ref
  hasLoadedExpenseFilterOptions = false;

  @observable
  pagination = new PaginationState("sources-edit", {
    onChangePagination: () => {
      if (!this.disclosureState.isOpen) return;
      if (this.sourcesModalType === BudgetItemTypeEnum.OurWorkBudgetItem) {
        this.fetchOiBudgetItems();
      } else {
        this.fetchExpenseItems();
      }
    },
  });

  @observable.ref
  displayedOiBudgetItems: OiBudgetItem[] = [];

  @observable.ref
  displayedExpenseItems: ExpenseItemForModal[] = [];

  /** Map to store composite key / id -> amount for calculating sum of selected items across pages */
  @observable
  itemsAmountMap = new Map<string, number>();

  expenseBudgetItemTypeMap = new Map<string, BudgetItemTypeEnum>();

  private fetchContractorOptions = async ({
    page,
    first,
    filters,
  }: {
    page: number;
    first: number;
    filters: { search: string };
  }) => {
    const [data, error] = await this.contractorOptionsFetcher.fetch({
      page,
      first,
      filters: {
        search: filters.search,
      },
    });

    if (error || !data.contractors) {
      return { options: [], paginatorInfo: undefined };
    }

    return {
      options: data.contractors.data.map(({ id, legal_name }) => ({
        value: id,
        label: legal_name,
      })),
      paginatorInfo: data.contractors.paginatorInfo,
    };
  };

  private mutateAmountFilter = (value: string[]): string[] => {
    if (!value.length || value[0] === "") return [];

    return [
      denominate(
        Number(value[0]),
        appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ).toString(),
    ];
  };

  oiBudgetItemFilters = new Filters<OiBudgetItemsTimeBillingWhereColumn>(
    [
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.ProjectId,
        hidden: true,
        operator: SqlOperator.Eq,
        options: [],
        value: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.ProjectTimeBillingPlanId,
        hidden: true,
        operator: SqlOperator.Eq,
        options: [],
        value: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.HasTimeBilling,
        hidden: true,
        operator: SqlOperator.Eq,
        options: [],
        value: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        title: t`Amount from (with VAT)`,
        options: [],
        mutateToGql: this.mutateAmountFilter,
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        title: t`Amount to (with VAT)`,
        options: [],
        mutateToGql: this.mutateAmountFilter,
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.DateOfSupply,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Date of supply`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.DueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Due date`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.IssueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Issue date`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.PaidAt,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Paid at`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: t`Payment status`,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.Type,
        operator: SqlOperator.In,
        title: t`Type`,
        options: [
          {
            label: t`Invoice`,
            value: OutgoingInvoiceTypeEnum.Invoice,
          },
          {
            label: t`Payment receipt`,
            value: OutgoingInvoiceTypeEnum.ProformaPayment,
          },
          {
            label: t`Credit note`,
            value: OutgoingInvoiceTypeEnum.CreditNote,
          },
        ],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: mapToOptions.workspaceCompanies(
          appStore.workspaceStore.companies,
        ),
      }),
    ],
    {
      onChange: () => {
        if (!this.disclosureState.isOpen) return;
        if (this.sourcesModalType !== BudgetItemTypeEnum.OurWorkBudgetItem) {
          return;
        }
        this.pagination.resetPage();
      },
    },
  );

  expenseItemFilters = new Filters<ExpenseItemsTimeBillingWhereColumn>(
    [
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.ProjectId,
        hidden: true,
        operator: SqlOperator.Eq,
        options: [],
        value: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.ProjectTimeBillingPlanId,
        hidden: true,
        operator: SqlOperator.Eq,
        options: [],
        value: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.HasTimeBilling,
        hidden: true,
        operator: SqlOperator.Eq,
        options: [],
        value: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        title: t`Amount from`,
        options: [],
        mutateToGql: this.mutateAmountFilter,
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        title: t`Amount to`,
        options: [],
        mutateToGql: this.mutateAmountFilter,
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.ContractorId,
        isPaginated: true,
        optionsQuery: this.fetchContractorOptions,
        operator: SqlOperator.In,
        title: t`Contractor`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.CreatedAt,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Created at`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.DateOfSupply,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Date of supply`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.DueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Due date`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.InvoiceStatus,
        operator: SqlOperator.In,
        title: t`Invoice status`,
        options: map(ExpenseInvoiceStatusEnum, (status) => ({
          label: upperFirst(lowerCase(status)),
          value: status,
        })),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.PaidAt,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Paid at`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: t`Payment status`,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.BudgetItemId,
        operator: SqlOperator.In,
        title: t`Budget item`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.BudgetItemType,
        operator: SqlOperator.In,
        title: t`Budget item type`,
        options: map(BudgetItemTypeEnum, (type) => ({
          label: upperFirst(lowerCase(type)),
          value: type,
        })),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.Status,
        hidden: true,
        operator: SqlOperator.Eq,
        options: [],
        value: ExpenseStatusEnum.Approved,
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.Type,
        operator: SqlOperator.In,
        title: t`Type`,
        options: map(ExpenseTypeEnum, (type) => {
          if (type === ExpenseTypeEnum.Card) {
            return {
              label: t`Card payments`,
              value: type,
            };
          }

          return {
            label: upperFirst(lowerCase(type)),
            value: type,
          };
        }),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: mapToOptions.workspaceCompanies(
          appStore.workspaceStore.companies,
        ),
      }),
    ],
    {
      onChange: () => {
        if (!this.disclosureState.isOpen) return;
        if (this.sourcesModalType !== BudgetItemTypeEnum.ExpenseBudgetItem) {
          return;
        }
        this.pagination.resetPage();
      },
    },
  );

  private oiBudgetItemFetcher = new FetchHelper<
    TimeBillingOiBudgetItemsForModalQuery,
    TimeBillingOiBudgetItemsForModalQueryVariables
  >(TimeBillingOiBudgetItemsForModalDocument);

  private expenseItemFetcher = new FetchHelper<
    TimeBillingExpenseItemsForModalQuery,
    TimeBillingExpenseItemsForModalQueryVariables
  >(TimeBillingExpenseItemsForModalDocument);

  private filterOptionsFetcher = new FetchHelper<
    TimeBillingSourcesEditModalFilterOptionsQuery,
    TimeBillingSourcesEditModalFilterOptionsQueryVariables
  >(TimeBillingSourcesEditModalFilterOptionsDocument);

  private contractorOptionsFetcher = new FetchHelper<
    ContractorSelectOptionsQuery,
    ContractorSelectOptionsQueryVariables
  >(ContractorSelectOptionsDocument);

  readonly disclosureState = new DisclosureState<DisclosureStateData>({
    onOpen: (data) => {
      if (!data) return;

      this.timeBillingPlanId = isUnsavedId(data.timeBillingPlanId)
        ? undefined
        : data.timeBillingPlanId;
      this.updateFilters();
      this.fetchFilterOptions();

      if (data.modalType === BudgetItemTypeEnum.OurWorkBudgetItem) {
        this.selectedOiBudgetItemKeys = data.sourceKeys;
        this.selectedExpenseItemIds = [];
      } else {
        this.selectedOiBudgetItemKeys = [];
        this.selectedExpenseItemIds = data.sourceKeys;
      }
      this.selectedTotalAmount = data.initialSelectedTotalAmount;

      this.displayedOiBudgetItems = [];
      this.displayedExpenseItems = [];
      this.itemsAmountMap.clear();
      this.expenseBudgetItemTypeMap.clear();
      this.pagination.resetPage();

      if (this.sourcesModalType === BudgetItemTypeEnum.OurWorkBudgetItem) {
        this.fetchOiBudgetItems();
      } else {
        this.fetchExpenseItems();
      }
    },
    onClose: () => {
      this.selectedOiBudgetItemKeys = [];
      this.selectedExpenseItemIds = [];
      this.displayedOiBudgetItems = [];
      this.displayedExpenseItems = [];
      this.itemsAmountMap.clear();
      this.expenseBudgetItemTypeMap.clear();
      this.selectedTotalAmount = 0;
      this.timeBillingPlanId = undefined;
    },
  });

  constructor(private readonly projectId: string) {
    makeObservable(this);

    this.oiBudgetItemFilters.filtersByColumn
      .get(OiBudgetItemsTimeBillingWhereColumn.ProjectId)
      ?.setValue(this.projectId, { skipOnChange: true });
    this.expenseItemFilters.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.ProjectId)
      ?.setValue(this.projectId, { skipOnChange: true });
    this.updateFilters();
  }

  @action.bound
  updateFilters(): void {
    const oiTimeBillingFilter = this.oiBudgetItemFilters.filtersByColumn.get(
      OiBudgetItemsTimeBillingWhereColumn.ProjectTimeBillingPlanId,
    );
    const oiHasTimeBillingFilter = this.oiBudgetItemFilters.filtersByColumn.get(
      OiBudgetItemsTimeBillingWhereColumn.HasTimeBilling,
    );

    const expenseTimeBillingFilter =
      this.expenseItemFilters.filtersByColumn.get(
        ExpenseItemsTimeBillingWhereColumn.ProjectTimeBillingPlanId,
      );
    const expenseHasTimeBillingFilter =
      this.expenseItemFilters.filtersByColumn.get(
        ExpenseItemsTimeBillingWhereColumn.HasTimeBilling,
      );

    if (this.timeBillingPlanId) {
      oiTimeBillingFilter?.setValue(this.timeBillingPlanId, {
        skipOnChange: true,
      });
      oiHasTimeBillingFilter?.setValue([], { skipOnChange: true });

      expenseTimeBillingFilter?.setValue(this.timeBillingPlanId, {
        skipOnChange: true,
      });
      expenseHasTimeBillingFilter?.setValue([], { skipOnChange: true });
    } else {
      oiTimeBillingFilter?.setValue([], { skipOnChange: true });
      oiHasTimeBillingFilter?.setValue("false", { skipOnChange: true });

      expenseTimeBillingFilter?.setValue([], { skipOnChange: true });
      expenseHasTimeBillingFilter?.setValue("false", { skipOnChange: true });
    }
  }

  @action.bound
  setOiBudgetItemSearchTerm(value: string): void {
    if (this.oiBudgetItemSearchTerm === value) return;
    this.oiBudgetItemSearchTerm = value;

    if (!this.disclosureState.isOpen) return;
    if (this.sourcesModalType !== BudgetItemTypeEnum.OurWorkBudgetItem) return;

    this.pagination.resetPage();
  }

  @action.bound
  handleOiBudgetItemOrderChange(orderBy: {
    column: OiBudgetItemsTimeBillingOrderByColumn;
    order: SortOrder | undefined;
  }): void {
    if (!orderBy.order) {
      this.oiBudgetItemOrderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }

    this.oiBudgetItemOrderBy.setClauses([
      { column: orderBy.column, order: orderBy.order },
    ]);
    this.pagination.resetPage();
  }

  @action.bound
  setExpenseSearchTerm(value: string): void {
    if (this.expenseSearchTerm === value) return;
    this.expenseSearchTerm = value;

    if (!this.disclosureState.isOpen) return;
    if (this.sourcesModalType !== BudgetItemTypeEnum.ExpenseBudgetItem) return;

    this.pagination.resetPage();
  }

  @action.bound
  handleExpenseOrderChange(orderBy: {
    column: ExpenseItemsTimeBillingOrderByColumn;
    order: SortOrder | undefined;
  }): void {
    if (!orderBy.order) {
      this.expenseOrderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }

    this.expenseOrderBy.setClauses([
      { column: orderBy.column, order: orderBy.order },
    ]);
    this.pagination.resetPage();
  }

  @action.bound
  setFilterOptions(data: TimeBillingSourcesEditModalFilterOptionsQuery): void {
    this.expenseItemFilters.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.BudgetItemId)
      ?.setOptions(
        mapToOptions.budgetItems(
          data.ourWorkBudgetItemsSimpleMap.concat(
            // @ts-expect-error - Typename mismatch but otherwise they are the same
            data.expenseBudgetItemsSimpleMap,
          ),
        ),
      );
  }

  @action.bound
  async fetchFilterOptions(): Promise<void> {
    if (this.hasLoadedExpenseFilterOptions) return;

    const [data, error] = await this.filterOptionsFetcher.fetch({
      projectId: this.projectId,
    });
    if (error || !data) return;

    this.setFilterOptions(data);
    this.hasLoadedExpenseFilterOptions = true;
  }

  @computed
  get sourcesModalType() {
    return this.disclosureState.additionalData?.modalType ?? null;
  }

  @computed
  get modalTitle() {
    return this.sourcesModalType === BudgetItemTypeEnum.OurWorkBudgetItem
      ? t`Edit Invoice Budget Items`
      : t`Edit Expense Items`;
  }

  @computed
  get isLoadingOiBudgetItems(): boolean {
    return this.oiBudgetItemFetcher.isLoading.value;
  }

  @computed
  get isLoadingExpenseItems(): boolean {
    return this.expenseItemFetcher.isLoading.value;
  }

  @action.bound
  async fetchOiBudgetItems(): Promise<void> {
    const [data, error] = await this.oiBudgetItemFetcher.fetch({
      filters: {
        page: this.pagination.currentPage,
        first: this.pagination.perPage,
        where: this.oiBudgetItemFilters.asGraphQueryParam,
        orderBy: this.oiBudgetItemOrderBy.clauses.length
          ? this.oiBudgetItemOrderBy.asWhereParam
          : undefined,
        search: this.oiBudgetItemSearchTerm || undefined,
      },
    });

    if (error || !data.oiBudgetItemsForTimeBilling) return;

    this.pagination.setFromPaginatorInfo(
      data.oiBudgetItemsForTimeBilling.paginatorInfo,
    );
    this.displayedOiBudgetItems = data.oiBudgetItemsForTimeBilling.data;

    data.oiBudgetItemsForTimeBilling.data.forEach((item) => {
      const key = encodeOiBudgetItemKey(
        item.outgoing_invoice_id,
        item.budget_item_id,
        item.budget_item_type as BudgetItemTypeEnum,
      );
      this.itemsAmountMap.set(key, item.amount);
    });
  }

  @action.bound
  async fetchExpenseItems(): Promise<void> {
    const [data, error] = await this.expenseItemFetcher.fetch({
      page: this.pagination.currentPage,
      first: this.pagination.perPage,
      filters: {
        where: this.expenseItemFilters.asGraphQueryParam,
        orderBy: this.expenseOrderBy.clauses.length
          ? this.expenseOrderBy.asWhereParam
          : undefined,
        search: this.expenseSearchTerm || undefined,
      },
    });

    if (error || !data.expenseItemsForTimeBilling) return;

    this.pagination.setFromPaginatorInfo(
      data.expenseItemsForTimeBilling.paginatorInfo,
    );
    this.displayedExpenseItems = data.expenseItemsForTimeBilling.data;

    data.expenseItemsForTimeBilling.data.forEach((item) => {
      this.itemsAmountMap.set(item.id, item.amount);
      if (item.budget_item_type) {
        this.expenseBudgetItemTypeMap.set(
          item.id,
          item.budget_item_type as unknown as BudgetItemTypeEnum,
        );
      }
    });
  }

  @action.bound
  setSelectedOiBudgetItemKeys(keys: string[]): void {
    this.updateSelectedTotalAmount(this.selectedOiBudgetItemKeys, keys);
    this.selectedOiBudgetItemKeys = keys;
  }

  @action.bound
  setSelectedExpenseItemIds(ids: string[]): void {
    this.updateSelectedTotalAmount(this.selectedExpenseItemIds, ids);
    this.selectedExpenseItemIds = ids;
  }

  @action.bound
  private updateSelectedTotalAmount(
    currentIds: string[],
    nextIds: string[],
  ): void {
    if (nextIds.length === 0) {
      this.selectedTotalAmount = 0;
      return;
    }

    const currentSet = new Set(currentIds);
    const nextSet = new Set(nextIds);

    let delta = 0;
    nextSet.forEach((id) => {
      if (!currentSet.has(id)) {
        delta += this.itemsAmountMap.get(id) ?? 0;
      }
    });
    currentSet.forEach((id) => {
      if (!nextSet.has(id)) {
        delta -= this.itemsAmountMap.get(id) ?? 0;
      }
    });

    this.selectedTotalAmount += delta;
  }

  @action.bound
  handleConfirm(): void {
    if (!this.sourcesModalType) {
      appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong, please try again later.`,
      });
      captureEvent({
        message:
          "FE: No sources modal type while trying to trigger sources edit modal confirm action.",
      });
      return;
    }

    if (this.sourcesModalType === BudgetItemTypeEnum.OurWorkBudgetItem) {
      this.disclosureState.additionalData?.onConfirm?.(
        this.selectedOiBudgetItemKeys,
        this.sourcesModalType,
      );
    } else {
      this.disclosureState.additionalData?.onConfirm?.(
        this.selectedExpenseItemIds,
        this.sourcesModalType,
      );
    }
    this.disclosureState.onClose();
  }
}
